// Market-Store — hält den letzten verarbeiteten FullData-Snapshot vom Worker-Cache.
// V1-Pendant: `fullData` State im useDataSync-Hook. In V3 als globaler Store damit
// mehrere Hooks (useDataSync, useBuildContext, useMacroHistory) dasselbe Snapshot lesen.
//
// Kein persist: Der Cache wird beim nächsten Sync (≤120s) eh überschrieben.

import { create } from 'zustand';
import { deriveMarketStats } from '@/lib/market-cache';
import type { FullData, MarketStats } from '@/types/market';

export interface MarketState {
  fullData: FullData | null;
  isSyncing: boolean;
  /** Unix-ms des letzten erfolgreichen Syncs. */
  lastSyncAt: number | null;
  /** Last sync error (für UI-Anzeige). */
  lastError: string | null;
}

export interface MarketActions {
  setFullData: (fullData: FullData | null) => void;
  setIsSyncing: (syncing: boolean) => void;
  setLastError: (err: string | null) => void;
  /** Bei Erfolg: fullData + lastSyncAt setzen, error löschen, isSyncing false. */
  markSyncSuccess: (fullData: FullData) => void;
  /** Bei Fehler: error setzen, isSyncing false. */
  markSyncError: (err: string) => void;
}

export type MarketStore = MarketState & MarketActions;

export const useMarketStore = create<MarketStore>()((set) => ({
  fullData: null,
  isSyncing: false,
  lastSyncAt: null,
  lastError: null,

  setFullData: (fullData) => set({ fullData }),
  setIsSyncing: (isSyncing) => set({ isSyncing }),
  setLastError: (lastError) => set({ lastError }),

  markSyncSuccess: (fullData) =>
    set({ fullData, isSyncing: false, lastSyncAt: Date.now(), lastError: null }),

  markSyncError: (err) => set({ isSyncing: false, lastError: err }),
}));

// ── Selektoren ───────────────────────────────────────────────────────────────

export const selectFullData = (s: MarketState): FullData | null => s.fullData;
export const selectIsSyncing = (s: MarketState): boolean => s.isSyncing;
export const selectLastSyncAt = (s: MarketState): number | null => s.lastSyncAt;
export const selectMarketStats = (s: MarketState): MarketStats | null =>
  deriveMarketStats(s.fullData);
